/**
 * useCorrespondenceInbox
 * Hook for managing inbox state, filtering, and row selection.
 */

import type { RowSelectionState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useCorrespondencePackageDestroyMutation,
  useCorrespondencePackageListQuery,
} from '@/services/api/correspondenceService';

import type {
  CorrespondencePackageSchema,
  CorrespondencePackageType,
  InboxFilters,
  PackageRowData,
  UnifiedStatus,
} from './types';

const DEFAULT_FILTERS: InboxFilters = {
  search: '',
  packageType: 'all',
  status: 'all',
};

/** Transform package to row data with computed fields */
const toRowData = (pkg: CorrespondencePackageSchema): PackageRowData => {
  const transmissionStatus = pkg.message?.transmission_status;
  const unifiedStatus: UnifiedStatus = transmissionStatus ?? pkg.status;

  let displayDate: string | null = null;
  let displayDateLabel = 'Created';

  if (pkg.transmitted_at) {
    displayDate = formatDate(pkg.transmitted_at);
    displayDateLabel = 'Sent';
  } else if (pkg.created_at) {
    displayDate = formatDate(pkg.created_at);
    displayDateLabel = 'Created';
  }

  return { ...pkg, unifiedStatus, displayDate, displayDateLabel };
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const useCorrespondenceInbox = (matterId: string) => {
  const [filters, setFilters] = useState<InboxFilters>(DEFAULT_FILTERS);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: packages = [], isLoading } = useCorrespondencePackageListQuery(
    { matterId },
    { skip: !matterId },
  );

  const [deletePackage, { isLoading: isDeleting }] =
    useCorrespondencePackageDestroyMutation();

  // Transform and filter packages
  const filteredPackages = useMemo(() => {
    let result = packages.map(toRowData);

    // Search filter
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((pkg) => {
        const typeLabel = pkg.package_type?.toLowerCase() ?? '';
        const recipient = pkg.recipient_display_name?.toLowerCase() ?? '';
        return typeLabel.includes(q) || recipient.includes(q);
      });
    }

    // Type filter
    if (filters.packageType !== 'all') {
      result = result.filter((pkg) => pkg.package_type === filters.packageType);
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter((pkg) => {
        const transmissionStatus = pkg.message?.transmission_status;
        return (
          pkg.status === filters.status || transmissionStatus === filters.status
        );
      });
    }

    // Sort newest first
    return result.sort((a, b) => {
      const dateA = a.transmitted_at || a.created_at || '';
      const dateB = b.transmitted_at || b.created_at || '';
      return dateB.localeCompare(dateA);
    });
  }, [packages, filters]);

  // Filter setters
  const setSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search }));
  }, []);

  const setPackageType = useCallback(
    (packageType: CorrespondencePackageType | 'all') => {
      setFilters((prev) => ({ ...prev, packageType }));
    },
    [],
  );

  const setStatus = useCallback((status: UnifiedStatus | 'all') => {
    setFilters((prev) => ({ ...prev, status }));
  }, []);

  const clearFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const hasActiveFilters =
    filters.search !== '' ||
    filters.packageType !== 'all' ||
    filters.status !== 'all';

  // Bulk delete
  const deleteSelected = useCallback(async () => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id],
    );

    const deletable = selectedIds.filter((id) => {
      const pkg = packages.find((p) => p.id === id);
      return pkg && !pkg.is_transmitted;
    });

    if (deletable.length === 0) {
      toast.error('No packages can be deleted');
      return;
    }

    try {
      await Promise.all(
        deletable.map((id) => deletePackage({ id, matterId }).unwrap()),
      );
      toast.success(
        `Deleted ${deletable.length} package${deletable.length > 1 ? 's' : ''}`,
      );
      setRowSelection({});
    } catch {
      toast.error('Failed to delete packages');
    }
  }, [rowSelection, packages, deletePackage, matterId]);

  return {
    packages,
    filteredPackages,
    isLoading,
    filters,
    setSearch,
    setPackageType,
    setStatus,
    clearFilters,
    hasActiveFilters,
    rowSelection,
    setRowSelection,
    deleteSelected,
    isDeleting,
  };
};
